import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ScrollText, ShieldAlert, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/** Full-screen blocking gate that appears the first time a pilot logs in
 *  (or any time they haven't accepted the current Terms & Conditions).
 *  The pilot must either ACCEPT (persisted server-side) or LOG OUT.
 *  Mounted globally in App.js — sits above every route including /terms. */
export default function TermsGate() {
  const { user, acceptTerms, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  if (!user) return null;
  if (user.terms_accepted_at) return null;

  const accept = async () => {
    if (!checked || busy) return;
    setBusy(true);
    try {
      await acceptTerms();
      toast.success("TERMS ACCEPTED");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "COULD NOT RECORD ACCEPTANCE");
    } finally { setBusy(false); }
  };

  const decline = () => {
    logout();
    toast("SESSION TERMINATED — TERMS NOT ACCEPTED");
  };

  const node = (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="terms-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-gate-title"
    >
      <div className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] p-5 md:p-6 w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={16} className="sat-cyan" />
          <span className="sat-label sat-cyan">MISSION CONTROL // TERMS &amp; CONDITIONS</span>
        </div>
        <h2 id="terms-gate-title" className="font-display font-bold text-2xl md:text-3xl mb-1">
          Before you fly, pilot.
        </h2>
        <p className="text-sm text-[color:var(--sat-text-dim)] mb-4">
          ASTRONATLAS is bound by the laws of India (IT Act 2000, IT Intermediary Rules 2021,
          DPDP Act 2023). Please review and accept our Terms &amp; Conditions to continue.
        </p>

        <div
          className="flex-1 overflow-y-auto border border-[color:var(--sat-border)] p-3 md:p-4 mb-4 font-mono text-[12px] leading-relaxed text-[color:var(--sat-text)]/90 space-y-3"
          data-testid="terms-gate-scroll"
        >
          <p><strong className="sat-cyan">1.</strong> You confirm that you are 13+ years old and legally competent to enter into an
            agreement in your jurisdiction.</p>
          <p><strong className="sat-cyan">2.</strong> ASTRONATLAS is an <em>intermediary</em> under Section 2(1)(w) of the IT Act, 2000.
            User-generated content (messages, posts, uploads) is your responsibility. We will remove any
            content that violates applicable Indian law or these Terms without prior notice.</p>
          <p><strong className="sat-cyan">3.</strong> Your email is a private identifier used for authentication and grievance
            handling only; it is <strong>never displayed publicly</strong>. Other pilots see only your callsign.</p>
          <p><strong className="sat-cyan">4.</strong> You may upload images, PDFs, spreadsheets, presentations, and documents
            up to 25 MB each. You represent that you own or have the right to share this content.
            You grant ASTRONATLAS a limited, non-exclusive licence to store and display it while your
            account is active.</p>
          <p><strong className="sat-cyan">5.</strong> Launch, rocket, astronaut, and news data comes from third-party APIs
            (The Space Devs, Spaceflight News API, Wikipedia). ASTRONATLAS does not warrant its
            accuracy and shall not be liable for reliance on it for any operational or safety-critical
            purpose.</p>
          <p><strong className="sat-cyan">6.</strong> To the maximum extent permitted by law, ASTRONATLAS, its founders,
            contributors, and hosting providers disclaim all warranties (express or implied) and total
            aggregate liability is capped at <strong>INR 1,000</strong>.</p>
          <p><strong className="sat-cyan">7.</strong> Personal data is processed under the DPDP Act, 2023. You may request access,
            correction, or deletion at any time by writing to the Grievance Officer listed in the full
            Terms.</p>
          <p><strong className="sat-cyan">8.</strong> Any dispute arising from these Terms is governed by Indian law and subject to
            the exclusive jurisdiction of the courts of New Delhi, India.</p>
          <p className="pt-2 border-t border-[color:var(--sat-border)]">
            The complete Terms are available at&nbsp;
            <Link
              to="/terms"
              className="sat-cyan hover:underline inline-flex items-center gap-1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="terms-gate-full-link"
            >
              /terms <ExternalLink size={10} />
            </Link>
            &nbsp;— we strongly recommend reading them in full.
          </p>
        </div>

        <label className="flex items-start gap-3 mb-4 cursor-pointer select-none" data-testid="terms-gate-check-label">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="terms-gate-check"
            className="mt-1 accent-[color:var(--sat-cyan)] w-4 h-4"
          />
          <span className="text-sm">
            I have read and agree to the ASTRONATLAS Terms &amp; Conditions and the processing of my
            personal data under the DPDP Act, 2023.
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-2 justify-end">
          <button
            onClick={decline}
            data-testid="terms-gate-decline"
            className="sat-btn !py-2.5 !px-4"
          >
            <LogOut size={12} /> DECLINE &amp; LOG OUT
          </button>
          <button
            onClick={accept}
            disabled={!checked || busy}
            data-testid="terms-gate-accept"
            className="sat-btn sat-btn-solid !py-2.5 !px-4 disabled:opacity-40"
          >
            <ScrollText size={12} /> {busy ? "TRANSMITTING…" : "ACCEPT & CONTINUE"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
